import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkles,
  UserPlus,
  LayoutDashboard,
  Mail,
  Sliders,
  Share2,
  Rocket,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  PartyPopper,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';
import { useContactsStore } from '@/stores/contactsStore';
import { getOnboardingState, markStepComplete, dismissOnboarding, type OnboardingState } from '@/lib/onboarding';

/* ------------------------------------------------------------------ */
/*  Step definitions                                                   */
/* ------------------------------------------------------------------ */

interface StepDef {
  id: number;
  title: string;
  description: string;
  icon: React.ElementType;
}

const STEPS: StepDef[] = [
  {
    id: 1,
    title: 'Welcome to Nexus',
    description: 'Nexus is your autonomous business operations platform. It manages contacts, email, support, finance, partnerships, and growth — powered by AI that learns your preferences over time.',
    icon: Sparkles,
  },
  {
    id: 2,
    title: 'Create your first contact',
    description: 'Contacts are at the heart of Nexus. Add someone you work with — a customer, partner, or lead — to see how the system organizes relationships automatically.',
    icon: UserPlus,
  },
  {
    id: 3,
    title: 'Explore the Dashboard',
    description: 'The Command Center gives you a real-time overview of every business domain — active deals, pending tickets, email volume, and AI-generated action items all in one place.',
    icon: LayoutDashboard,
  },
  {
    id: 4,
    title: 'Send a test email',
    description: 'Email Ops lets Nexus classify, prioritize, and even draft replies for your inbox. Head over to explore smart templates, scheduling, and automated follow-ups.',
    icon: Mail,
  },
  {
    id: 5,
    title: 'Review AI Settings',
    description: 'Control how autonomous Nexus is. Set boundaries per module — from fully manual to fully autonomous — and adjust confidence thresholds to match your comfort level.',
    icon: Sliders,
  },
  {
    id: 6,
    title: 'Explore Context Graph',
    description: 'The Context Graph maps every relationship, interaction, and data point into a living knowledge network. See how contacts, deals, and events connect to each other.',
    icon: Share2,
  },
  {
    id: 7,
    title: "You're Ready!",
    description: 'You have completed the setup. Nexus is now configured and ready to help you run your business. Dive into your daily briefing to see what needs attention today.',
    icon: Rocket,
  },
];

/* ------------------------------------------------------------------ */
/*  Inline contact form (Step 2)                                       */
/* ------------------------------------------------------------------ */

function InlineContactForm({ onCreated }: { onCreated: () => void }) {
  const createContact = useContactsStore((s) => s.create);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [type, setType] = useState<'customer' | 'lead' | 'partner'>('lead');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await createContact({ name: name.trim(), email: email.trim() || undefined, type });
      onCreated();
    } catch (err) {
      setError((err as Error).message || 'Failed to create contact');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-5 space-y-3">
      <div>
        <label className="block text-xs font-medium text-surface-400 mb-1">Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Smith"
          required
          className="w-full rounded-lg border border-surface-600 bg-surface-800/80 px-3 py-2 text-sm text-surface-100 placeholder:text-surface-500 focus:border-nexus-500 focus:outline-none focus:ring-1 focus:ring-nexus-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-surface-400 mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane@company.com"
          className="w-full rounded-lg border border-surface-600 bg-surface-800/80 px-3 py-2 text-sm text-surface-100 placeholder:text-surface-500 focus:border-nexus-500 focus:outline-none focus:ring-1 focus:ring-nexus-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-surface-400 mb-1">Type</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as 'customer' | 'lead' | 'partner')}
          className="w-full rounded-lg border border-surface-600 bg-surface-800/80 px-3 py-2 text-sm text-surface-100 focus:border-nexus-500 focus:outline-none focus:ring-1 focus:ring-nexus-500"
        >
          <option value="lead">Lead</option>
          <option value="customer">Customer</option>
          <option value="partner">Partner</option>
        </select>
      </div>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <Button type="submit" size="sm" loading={submitting} icon={<UserPlus className="h-3.5 w-3.5" />}>
        Create Contact
      </Button>
    </form>
  );
}

/* ------------------------------------------------------------------ */
/*  Step action areas                                                  */
/* ------------------------------------------------------------------ */

function StepAction({
  stepId,
  completed,
  onComplete,
}: {
  stepId: number;
  completed: boolean;
  onComplete: () => void;
}) {
  const navigate = useNavigate();

  if (completed) {
    return (
      <div className="mt-5 flex items-center gap-2 text-nexus-400">
        <CheckCircle2 className="h-4 w-4" />
        <span className="text-sm font-medium">Completed</span>
      </div>
    );
  }

  switch (stepId) {
    case 1:
      return (
        <div className="mt-5">
          <Button onClick={onComplete} icon={<ArrowRight className="h-4 w-4" />}>
            Let's get started
          </Button>
        </div>
      );

    case 2:
      return <InlineContactForm onCreated={onComplete} />;

    case 3:
      return (
        <div className="mt-5">
          <Button
            onClick={() => {
              onComplete();
              navigate('/');
            }}
            icon={<LayoutDashboard className="h-4 w-4" />}
          >
            Go to Dashboard
          </Button>
        </div>
      );

    case 4:
      return (
        <div className="mt-5">
          <Button
            onClick={() => {
              onComplete();
              navigate('/email');
            }}
            icon={<Mail className="h-4 w-4" />}
          >
            Open Email Ops
          </Button>
        </div>
      );

    case 5:
      return (
        <div className="mt-5">
          <Button
            onClick={() => {
              onComplete();
              navigate('/settings');
            }}
            icon={<Sliders className="h-4 w-4" />}
          >
            Open AI Settings
          </Button>
        </div>
      );

    case 6:
      return (
        <div className="mt-5">
          <Button
            onClick={() => {
              onComplete();
              navigate('/context');
            }}
            icon={<Share2 className="h-4 w-4" />}
          >
            Explore Context Graph
          </Button>
        </div>
      );

    case 7:
      return (
        <div className="mt-5">
          <Button onClick={() => navigate('/today')} icon={<Rocket className="h-4 w-4" />}>
            Go to Today
          </Button>
        </div>
      );

    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

export function GettingStarted() {
  const { user, tenant } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<OnboardingState>(getOnboardingState);

  // Allow deep-linking to a specific step via ?step=3
  const initialStep = (() => {
    const p = searchParams.get('step');
    if (p) {
      const n = parseInt(p, 10);
      if (n >= 1 && n <= 7) return n;
    }
    // Default: first incomplete step, or 1
    for (let i = 1; i <= 7; i++) {
      if (!state.completedSteps.includes(i)) return i;
    }
    return 1;
  })();

  const [currentStep, setCurrentStep] = useState(initialStep);

  // Keep state fresh from localStorage
  const refreshState = useCallback(() => {
    setState(getOnboardingState());
  }, []);

  const completedCount = state.completedSteps.length;
  const step = STEPS[currentStep - 1];
  const isCompleted = state.completedSteps.includes(currentStep);

  const handleComplete = useCallback(() => {
    markStepComplete(currentStep);
    refreshState();
    // Auto-advance after a brief pause (except on last step)
    if (currentStep < 7) {
      setTimeout(() => setCurrentStep((s) => Math.min(s + 1, 7)), 400);
    }
  }, [currentStep, refreshState]);

  const handleSkip = () => {
    dismissOnboarding();
    navigate('/');
  };

  // Celebration for step 7
  const isCelebration = currentStep === 7;

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center px-4 py-12">
      {/* Background gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-nexus-600/6 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[400px] bg-blue-600/4 rounded-full blur-3xl" />
        {isCelebration && (
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-nexus-500/8 rounded-full blur-3xl animate-pulse" />
        )}
      </div>

      <div className="relative w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-black text-surface-100 tracking-tight">
            {isCelebration ? (
              <span className="flex items-center justify-center gap-3">
                <PartyPopper className="h-8 w-8 text-nexus-400" />
                You're all set!
              </span>
            ) : (
              <>Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}</>
            )}
          </h1>
          {!isCelebration && (
            <p className="mt-2 text-surface-400 text-sm">
              {tenant?.name ? `${tenant.name} is ready.` : 'Your workspace is ready.'} Let's walk through the basics.
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-surface-500">{completedCount}/7 complete</span>
            <span className="text-xs text-surface-500">Step {currentStep} of 7</span>
          </div>
          <div className="h-1.5 w-full bg-surface-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-nexus-600 to-nexus-400 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${(completedCount / 7) * 100}%` }}
            />
          </div>
          {/* Step dots */}
          <div className="flex items-center justify-between mt-3 px-1">
            {STEPS.map((s) => {
              const done = state.completedSteps.includes(s.id);
              const active = s.id === currentStep;
              return (
                <button
                  key={s.id}
                  onClick={() => setCurrentStep(s.id)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-all cursor-pointer ${
                    done
                      ? 'bg-nexus-600/20 text-nexus-400 border border-nexus-500/30'
                      : active
                        ? 'bg-surface-700 text-surface-100 border border-surface-500 ring-2 ring-nexus-500/30'
                        : 'bg-surface-800 text-surface-500 border border-surface-700 hover:border-surface-600'
                  }`}
                  aria-label={`Go to step ${s.id}`}
                >
                  {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : s.id}
                </button>
              );
            })}
          </div>
        </div>

        {/* Current step card */}
        <div
          key={currentStep}
          className="bg-surface-800/80 backdrop-blur-sm border border-surface-700 rounded-xl p-8 animate-in fade-in slide-in-from-bottom-2 duration-300"
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                isCompleted ? 'bg-nexus-600/20' : 'bg-surface-700'
              }`}
            >
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5 text-nexus-400" />
              ) : (
                <step.icon className="h-5 w-5 text-surface-200" />
              )}
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase tracking-wider text-surface-500">
                Step {step.id}
              </span>
              <h2 className="text-lg font-bold text-surface-100 leading-tight">{step.title}</h2>
            </div>
          </div>

          <p className="text-sm text-surface-400 leading-relaxed">{step.description}</p>

          <StepAction stepId={currentStep} completed={isCompleted} onComplete={handleComplete} />
        </div>

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setCurrentStep((s) => Math.max(s - 1, 1))}
            disabled={currentStep === 1}
            className="flex items-center gap-1 text-sm text-surface-400 hover:text-surface-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            <ChevronLeft className="h-4 w-4" /> Previous
          </button>

          <button
            onClick={handleSkip}
            className="text-xs text-surface-500 hover:text-surface-300 cursor-pointer transition-colors"
          >
            Skip setup
          </button>

          <button
            onClick={() => setCurrentStep((s) => Math.min(s + 1, 7))}
            disabled={currentStep === 7}
            className="flex items-center gap-1 text-sm text-surface-400 hover:text-surface-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Tip */}
        <div className="mt-8 text-center">
          <p className="text-xs text-surface-600">You can always come back to this guide from Settings.</p>
        </div>
      </div>
    </div>
  );
}

export function shouldShowGuide(): boolean {
  return !getOnboardingState().dismissed;
}
